import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, apiService } from "../services/api";
import Layout from "../components/Layout";
import {
  Plus,
  FileSearch,
  MoreVertical,
  Trash2,
  Play,
  Eye,
  Loader2,
  Search,
  Sparkles,
  Database,
  ChevronRight,
  X,
  Save,
  Code,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { toast } from "sonner";

// WILL AI Chat Component
const WillChat = ({ connectors, onExtractionCreated, onClose }) => {
  const { token } = useAuth();
  const [selectedConnector, setSelectedConnector] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState("");

  const handleSend = async () => {
    if (!message.startsWith("WILL ")) {
      setError('A mensagem deve começar com "WILL " para ativar a IA');
      return;
    }
    if (!selectedConnector) {
      setError("Selecione um conector");
      return;
    }

    setLoading(true);
    setError("");
    setResponse(null);

    try {
      const result = await apiService.askWill(
        selectedConnector,
        message,
        {},
        token,
      );
      setResponse(result.data);
    } catch (err) {
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError("Erro ao processar solicitação da IA");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveExtraction = async () => {
    if (!response?.builder_spec_json) return;

    try {
      const extractionData = {
        name: `Extração WILL - ${new Date().toLocaleString("pt-BR")}`,
        description: message,
        connector_id: selectedConnector,
        builder_spec_json: response.builder_spec_json,
      };

      await apiService.createExtraction(extractionData, token);
      toast.success("Extração salva com sucesso!");
      onExtractionCreated?.();
      onClose?.();
    } catch (error) {
      toast.error("Erro ao salvar extração");
    }
  };
return (
    <div className="flex flex-col h-full bg-white">
      {/* Área de Input Fixa no Topo */}
      <div className="p-4 sm:p-6 border-b border-slate-200 shrink-0">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full will-gradient flex items-center justify-center will-glow">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-slate-900">WILL</h3>
            <p className="text-xs text-slate-500">Assistente de IA</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Conector</Label>
            <Select
              value={selectedConnector}
              onValueChange={setSelectedConnector}
            >
              <SelectTrigger data-testid="will-connector-select">
                <SelectValue placeholder="Selecione um conector" />
              </SelectTrigger>
              <SelectContent>
                {connectors.map((conn) => (
                  <SelectItem key={conn.id} value={conn.id}>
                    {conn.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>O que você deseja criar?</Label>
            <Textarea
              placeholder="WILL quero um dashboard de vendas por produto no mês atual"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="min-h-[100px] resize-none"
              data-testid="will-message-input"
            />
            <p className="text-xs text-slate-500">
              Comece com{" "}
              <span className="font-mono bg-violet-100 text-violet-700 px-1 rounded">
                WILL
              </span>{" "}
              para ativar a IA
            </p>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
              {error}
            </div>
          )}

          <Button
            onClick={handleSend}
            disabled={loading || !selectedConnector}
            className="w-full btn-will"
            data-testid="will-send-btn"
          >
            {loading ? (
              <>
                <div className="flex items-center gap-1 mr-2">
                  <span className="typing-dot" />
                  <span className="typing-dot" />
                  <span className="typing-dot" />
                </div>
                Processando...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Gerar com WILL
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Response Area */}
      {response && (
        <>
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-slate-50/50 min-h-0">
            <div className="space-y-6">
              {response.warnings?.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg shadow-sm">
                  <p className="text-sm font-medium text-amber-800">Avisos:</p>
                  <ul className="text-sm text-amber-700 mt-1 list-disc list-inside pl-1">
                    {response.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Preview Data */}
              {response.preview?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-slate-900">
                    Preview dos Dados
                  </h4>
                  <div className="border border-slate-200 rounded-lg overflow-x-auto bg-white shadow-sm">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          {Object.keys(response.preview[0]).map((key) => (
                            <th
                              key={key}
                              className="px-3 py-2 text-left font-medium text-slate-600 border-b whitespace-nowrap"
                            >
                              {key}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {response.preview.slice(0, 10).map((row, i) => (
                          <tr key={i} className="hover:bg-slate-50 transition-colors">
                            {Object.values(row).map((val, j) => (
                              <td
                                key={j}
                                className="px-3 py-2 border-b border-slate-100 tabular-nums whitespace-nowrap"
                              >
                                {typeof val === "number"
                                  ? val.toLocaleString("pt-BR")
                                  : String(val)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Suggested Widgets */}
              {response.suggested_widgets?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-slate-900">
                    Widgets Sugeridos
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {response.suggested_widgets.map((widget, i) => (
                      <div
                        key={i}
                        className="p-3 bg-violet-50 border border-violet-200 rounded-lg shadow-sm"
                      >
                        <p className="font-medium text-violet-800 truncate" title={widget.title}>
                          {widget.title}
                        </p>
                        <p className="text-xs text-violet-600 mt-1">
                          Tipo: <span className="font-semibold uppercase">{widget.type}</span>
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AST Preview */}
              {response.builder_spec_json &&
                Object.keys(response.builder_spec_json).length > 0 && (
                  <div className="space-y-2 pb-2">
                    <h4 className="font-medium text-slate-900">
                      Especificação Gerada (AST)
                    </h4>
                    <pre className="p-3 bg-slate-900 text-slate-100 rounded-lg text-xs overflow-x-auto max-h-[150px] font-mono whitespace-pre-wrap break-words shadow-inner">
                      {JSON.stringify(response.builder_spec_json, null, 2)}
                    </pre>
                  </div>
                )}
            </div>
          </div>

          {/* Action Footer */}
          <div className="p-4 border-t border-slate-200 bg-white shrink-0">
            <Button
              onClick={handleSaveExtraction}
              className="w-full bg-indigo-600 hover:bg-indigo-700 shadow-sm"
              data-testid="will-save-extraction-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Salvar Extração
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

// Visual Query Builder Component
const QueryBuilder = ({ connectors, onSave, onClose }) => {
  const { token } = useAuth();
  const [selectedConnector, setSelectedConnector] = useState("");
  const [schema, setSchema] = useState(null);
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [extractionName, setExtractionName] = useState("");
  const [selectedTable, setSelectedTable] = useState("");
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [aggregations, setAggregations] = useState([]);
  const [groupBy, setGroupBy] = useState([]);
  const [limit, setLimit] = useState(1000);
  const [previewData, setPreviewData] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchSchema = async (connectorId) => {
    setLoadingSchema(true);
    try {
      const response = await apiService.introspectConnector(connectorId, token);
      setSchema(response.data);
    } catch (error) {
      toast.error("Erro ao carregar schema");
    } finally {
      setLoadingSchema(false);
    }
  };

  useEffect(() => {
    if (selectedConnector) {
      fetchSchema(selectedConnector);
      setSelectedTable("");
      setSelectedColumns([]);
      setAggregations([]);
      setGroupBy([]);
    }
  }, [selectedConnector]);

  const handlePreview = async () => {
    if (!selectedTable) return;

    setPreviewing(true);
    try {
      const builderSpec = buildAST();

      const response = await apiService.createExtraction(
        {
          name: `temp_${Date.now()}`,
          connector_id: selectedConnector,
          builder_spec_json: builderSpec,
        },
        token,
      );

      const previewResponse = await apiService.previewExtraction(
        response.data.id,
        token,
      );
      setPreviewData(previewResponse.data);

      await apiService.deleteExtraction(response.data.id, token);
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(`Erro: ${error.response.data.detail}`);
      } else {
        toast.error("Erro ao gerar preview");
      }
    } finally {
      setPreviewing(false);
    }
  };

  const buildAST = () => {
    return {
      table: selectedTable,
      columns: selectedColumns.filter(
        (c) => !aggregations.find((a) => a.column === c),
      ),
      aggregations: aggregations.map((a) => ({
        function: a.function,
        column: a.column,
        alias: `${a.function}_${a.column}`,
      })),
      group_by: groupBy,
      order_by: [],
      limit: limit,
    };
  };

  const handleSave = async () => {
    if (!extractionName.trim() || !selectedTable) {
      toast.error("Preencha o nome e selecione uma tabela");
      return;
    }

    setSaving(true);
    try {
      await apiService.createExtraction(
        {
          name: extractionName,
          connector_id: selectedConnector,
          builder_spec_json: buildAST(),
        },
        token,
      );
      toast.success("Extração criada com sucesso!");
      onSave?.();
      onClose?.();
    } catch (error) {
      toast.error("Erro ao criar extração");
    } finally {
      setSaving(false);
    }
  };

  const tableColumns =
    schema?.tables?.find((t) => t.name === selectedTable)?.columns || [];

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Cabeçalho do Builder */}
      <div className="p-4 sm:p-6 border-b border-slate-200 space-y-4 shrink-0">
        <div className="space-y-2">
          <Label>Nome da Extração</Label>
          <Input
            placeholder="Ex: Vendas por Produto"
            value={extractionName}
            onChange={(e) => setExtractionName(e.target.value)}
            data-testid="extraction-name-input"
          />
        </div>

        <div className="space-y-2">
          <Label>Conector</Label>
          <Select
            value={selectedConnector}
            onValueChange={setSelectedConnector}
          >
            <SelectTrigger data-testid="builder-connector-select">
              <SelectValue placeholder="Selecione um conector" />
            </SelectTrigger>
            <SelectContent>
              {connectors.map((conn) => (
                <SelectItem key={conn.id} value={conn.id}>
                  {conn.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        {loadingSchema ? (
          <div className="flex items-center justify-center py-10 h-full">
            <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
          </div>
        ) : schema ? (
          <div className="p-4 sm:p-6 space-y-6">
            {/* Table Selection */}
            <div className="space-y-2">
              <Label>Tabela Principal</Label>
              <Select
                value={selectedTable}
                onValueChange={(v) => {
                  setSelectedTable(v);
                  setSelectedColumns([]);
                  setAggregations([]);
                  setGroupBy([]);
                }}
              >
                <SelectTrigger data-testid="table-select">
                  <SelectValue placeholder="Selecione uma tabela" />
                </SelectTrigger>
                <SelectContent>
                  {schema.tables?.map((table) => (
                    <SelectItem key={table.name} value={table.name}>
                      {table.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedTable && (
              <>
                {/* Column Selection */}
                <div className="space-y-2">
                  <Label>Colunas</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[200px] overflow-y-auto p-2 border border-slate-200 rounded-lg bg-slate-50/50">
                    {tableColumns.map((col) => (
                      <label
                        key={col.name}
                        className="flex items-center gap-2 p-2 hover:bg-white rounded cursor-pointer border border-transparent hover:border-slate-200 transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedColumns.includes(col.name)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedColumns([
                                ...selectedColumns,
                                col.name,
                              ]);
                            } else {
                              setSelectedColumns(
                                selectedColumns.filter((c) => c !== col.name),
                              );
                            }
                          }}
                          className="rounded border-slate-300 w-4 h-4 text-indigo-600"
                        />
                        <span
                          className="text-sm font-medium text-slate-700 truncate flex-1"
                          title={col.name}
                        >
                          {col.name}
                        </span>
                        <span className="text-xs text-slate-400 font-mono shrink-0">
                          {col.type}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Aggregations */}
                <div className="space-y-3">
                  <Label>Agregações</Label>
                  <div className="space-y-2">
                    {aggregations.map((agg, i) => (
                      <div
                        key={i}
                        className="flex flex-wrap sm:flex-nowrap gap-2 items-center bg-slate-50 p-2 border border-slate-200 rounded-lg"
                      >
                        <Select
                          value={agg.function}
                          onValueChange={(v) => {
                            const newAggs = [...aggregations];
                            newAggs[i].function = v;
                            setAggregations(newAggs);
                          }}
                        >
                          <SelectTrigger className="w-full sm:w-32 bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="SUM">SUM</SelectItem>
                            <SelectItem value="COUNT">COUNT</SelectItem>
                            <SelectItem value="AVG">AVG</SelectItem>
                            <SelectItem value="MIN">MIN</SelectItem>
                            <SelectItem value="MAX">MAX</SelectItem>
                          </SelectContent>
                        </Select>

                        <Select
                          value={agg.column}
                          onValueChange={(v) => {
                            const newAggs = [...aggregations];
                            newAggs[i].column = v;
                            setAggregations(newAggs);
                          }}
                        >
                          <SelectTrigger className="flex-1 bg-white">
                            <SelectValue placeholder="Coluna" />
                          </SelectTrigger>
                          <SelectContent>
                            {tableColumns.map((col) => (
                              <SelectItem key={col.name} value={col.name}>
                                {col.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-slate-400 hover:text-red-600 shrink-0"
                          onClick={() => {
                            setAggregations(
                              aggregations.filter((_, j) => j !== i),
                            );
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto"
                    onClick={() =>
                      setAggregations([
                        ...aggregations,
                        { function: "SUM", column: "" },
                      ])
                    }
                  >
                    <Plus className="w-4 h-4 mr-1" /> Adicionar Agregação
                  </Button>
                </div>

                {/* Group By */}
                {aggregations.length > 0 && (
                  <div className="space-y-2">
                    <Label>Agrupar por</Label>
                    <div className="flex flex-wrap gap-2">
                      {selectedColumns
                        .filter(
                          (c) => !aggregations.find((a) => a.column === c),
                        )
                        .map((col) => (
                          <label
                            key={col}
                            className="flex items-center gap-2 p-2 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={groupBy.includes(col)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setGroupBy([...groupBy, col]);
                                } else {
                                  setGroupBy(groupBy.filter((g) => g !== col));
                                }
                              }}
                              className="rounded border-slate-300 text-indigo-600"
                            />
                            <span className="text-sm font-medium">{col}</span>
                          </label>
                        ))}
                    </div>
                  </div>
                )}

                {/* Limit */}
                <div className="space-y-2">
                  <Label>Limite de Registros</Label>
                  <Input
                    type="number"
                    value={limit}
                    onChange={(e) =>
                      setLimit(
                        Math.min(
                          5000,
                          Math.max(1, parseInt(e.target.value) || 1000),
                        ),
                      )
                    }
                    className="w-full sm:w-32"
                  />
                </div>

                {previewData && (
                  <div className="space-y-2 mt-6">
                    <h4 className="font-medium text-slate-900">
                      Preview ({previewData.row_count} registros)
                    </h4>
                    <div className="border border-slate-200 rounded-lg overflow-x-auto bg-white">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            {previewData.data[0] &&
                              Object.keys(previewData.data[0]).map((key) => (
                                <th
                                  key={key}
                                  className="px-3 py-2 text-left font-medium text-slate-600 border-b whitespace-nowrap"
                                >
                                  {key}
                                </th>
                              ))}
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.data.slice(0, 10).map((row, i) => (
                            <tr
                              key={i}
                              className="hover:bg-slate-50 transition-colors"
                            >
                              {Object.values(row).map((val, j) => (
                                <td
                                  key={j}
                                  className="px-3 py-2 border-b border-slate-100 tabular-nums whitespace-nowrap"
                                >
                                  {typeof val === "number"
                                    ? val.toLocaleString("pt-BR")
                                    : String(val)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-slate-400 p-6 text-center">
            Selecione um conector para começar a montar sua extração
          </div>
        )}
      </div>

      {/* Actions / Footer */}
      <div className="p-4 border-t border-slate-200 flex flex-col-reverse sm:flex-row gap-3 shrink-0 bg-slate-50">
        <Button
          variant="outline"
          className="w-full sm:flex-1"
          onClick={handlePreview}
          disabled={!selectedTable || previewing}
        >
          {previewing ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Eye className="w-4 h-4 mr-2" />
          )}
          Preview
        </Button>
        <Button
          className="w-full sm:flex-1 bg-indigo-600 hover:bg-indigo-700"
          onClick={handleSave}
          disabled={!extractionName.trim() || !selectedTable || saving}
          data-testid="save-extraction-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Salvar Extração
        </Button>
      </div>
    </div>
  );
};

const ExtractionsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const [extractions, setExtractions] = useState([]);
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedExtraction, setSelectedExtraction] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Create modes
  const [createSheetOpen, setCreateSheetOpen] = useState(false);
  const [createMode, setCreateMode] = useState("visual"); // 'visual' or 'will'

  const fetchData = async () => {
    try {
      const [extractionsRes, connectorsRes] = await Promise.all([
        apiService.getExtractions(token),
        apiService.getConnectors(token),
      ]);
      setExtractions(extractionsRes.data);
      setConnectors(connectorsRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Open WILL if URL param
    if (searchParams.get("will") === "true") {
      setCreateMode("will");
      setCreateSheetOpen(true);
    }
  }, [token, searchParams]);

  const handleDelete = async () => {
    if (!selectedExtraction) return;
    try {
      await apiService.deleteExtraction(selectedExtraction.id, token);
      toast.success("Extração excluída");
      setDeleteDialogOpen(false);
      setSelectedExtraction(null);
      fetchData();
    } catch (error) {
      toast.error("Erro ao excluir extração");
    }
  };

  const handlePreview = async (extraction) => {
    setSelectedExtraction(extraction);
    setPreviewDialogOpen(true);
    setPreviewLoading(true);
    setPreviewData(null);

    try {
      const response = await apiService.previewExtraction(extraction.id, token);
      setPreviewData(response.data);
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(`Erro: ${error.response.data.detail}`);
      } else {
        toast.error("Erro ao gerar preview");
      }
      setPreviewDialogOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const filteredExtractions = extractions.filter((e) =>
    e.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <Layout>
      <div className="animate-fadeIn">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-slate-900">
              Extrações
            </h1>
            <p className="text-slate-500 mt-1">
              Gerencie suas extrações de dados
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={() => {
                setCreateMode("visual");
                setCreateSheetOpen(true);
              }}
              className="w-full sm:w-auto flex items-center justify-center"
              data-testid="create-extraction-visual-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Criar Visual
            </Button>
            <Button
              onClick={() => {
                setCreateMode("will");
                setCreateSheetOpen(true);
              }}
              className="btn-will w-full sm:w-auto flex items-center justify-center"
              data-testid="create-extraction-will-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Criar com WILL
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar extrações..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 max-w-md"
          />
        </div>

        {/* No Connectors Warning */}
        {connectors.length === 0 && !loading && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
            <Database className="w-5 h-5 text-amber-600" />
            <p className="text-sm text-amber-800">
              Você precisa criar um conector antes de criar extrações.{" "}
              <button
                onClick={() => navigate("/settings")}
                className="font-medium underline"
              >
                Ir para Configurações
              </button>
            </p>
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
          </div>
        ) : filteredExtractions.length === 0 ? (
          /* Empty State */
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
              <FileSearch className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="font-heading text-lg font-medium text-slate-900 mb-2">
              {searchTerm
                ? "Nenhuma extração encontrada"
                : "Nenhuma extração ainda"}
            </h3>
            <p className="text-slate-500 mb-6">
              {searchTerm
                ? "Tente uma busca diferente"
                : "Crie sua primeira extração para começar"}
            </p>
          </div>
        ) : (
          /* Extractions List */
          <div className="space-y-3">
            {filteredExtractions.map((extraction, index) => (
              <div
                key={extraction.id}
                className="card p-4 flex items-center gap-4 animate-fadeIn"
                style={{ animationDelay: `${index * 30}ms` }}
              >
                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  <FileSearch className="w-5 h-5 text-indigo-600" />
                </div>

                <div className="flex-1 min-w-0">
                  <h3
                    className="font-medium text-slate-900 truncate"
                    data-testid={`extraction-name-${extraction.id}`}
                  >
                    {extraction.name}
                  </h3>
                  <p className="text-sm text-slate-500 truncate">
                    {extraction.description || "Sem descrição"}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handlePreview(extraction)}
                    data-testid={`preview-extraction-${extraction.id}`}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Preview
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-2 hover:bg-slate-100 rounded-lg">
                        <MoreVertical className="w-4 h-4 text-slate-500" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => handlePreview(extraction)}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Preview
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() => {
                          setSelectedExtraction(extraction);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Excluir
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Sheet */}
        <Sheet open={createSheetOpen} onOpenChange={setCreateSheetOpen}>
          <SheetContent className="w-full sm:max-w-xl p-0">
            <Tabs
              value={createMode}
              onValueChange={(v) => setCreateMode(v)}
              className="h-full flex flex-col"
            >
              <SheetHeader className="p-6 pb-0">
                <SheetTitle>Nova Extração</SheetTitle>
                <SheetDescription>
                  Crie uma nova extração de dados
                </SheetDescription>
                <TabsList className="grid w-full grid-cols-2 mt-4">
                  <TabsTrigger
                    value="visual"
                    className="flex items-center gap-2"
                  >
                    <Code className="w-4 h-4" />
                    Visual Builder
                  </TabsTrigger>
                  <TabsTrigger value="will" className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    WILL IA
                  </TabsTrigger>
                </TabsList>
              </SheetHeader>

              <TabsContent
                value="visual"
                className="flex-1 overflow-hidden m-0"
              >
                <QueryBuilder
                  connectors={connectors}
                  onSave={fetchData}
                  onClose={() => setCreateSheetOpen(false)}
                />
              </TabsContent>

              <TabsContent value="will" className="flex-1 overflow-hidden m-0">
                <WillChat
                  connectors={connectors}
                  onExtractionCreated={fetchData}
                  onClose={() => setCreateSheetOpen(false)}
                />
              </TabsContent>
            </Tabs>
          </SheetContent>
        </Sheet>

        {/* Delete Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="w-[95vw] sm:w-full max-w-md">
            <DialogHeader>
              <DialogTitle>Excluir Extração</DialogTitle>
              <DialogDescription>
                Tem certeza que deseja excluir "{selectedExtraction?.name}"?
                Esta ação não pode ser desfeita.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0 mt-4">
              <Button
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => setDeleteDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                className="w-full sm:w-auto"
                onClick={handleDelete}
              >
                Excluir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
          <DialogContent className="max-w-4xl w-[95vw] sm:w-full max-h-[90vh] sm:max-h-[85vh] flex flex-col p-4 sm:p-6 overflow-hidden">
            <DialogHeader className="shrink-0 mb-2">
              <DialogTitle>Preview: {selectedExtraction?.name}</DialogTitle>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto min-h-0 pr-1">
              {previewLoading ? (
                <div className="flex items-center justify-center py-10 h-full">
                  <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
                </div>
              ) : previewData ? (
                <div className="space-y-4 pb-2">
                  <p className="text-sm text-slate-500">
                    {previewData.row_count} registros encontrados
                  </p>

                  {previewData.data && previewData.data.length > 0 ? (
                    <div className="border border-slate-200 rounded-lg overflow-x-auto bg-white">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            {Object.keys(previewData.data[0]).map((key) => (
                              <th
                                key={key}
                                className="px-3 py-2 text-left font-medium text-slate-600 border-b whitespace-nowrap"
                              >
                                {key}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.data.map((row, i) => (
                            <tr
                              key={i}
                              className="hover:bg-slate-50 transition-colors"
                            >
                              {Object.values(row).map((val, j) => (
                                <td
                                  key={j}
                                  className="px-3 py-2 border-b border-slate-100 tabular-nums whitespace-nowrap"
                                >
                                  {typeof val === "number"
                                    ? val.toLocaleString("pt-BR")
                                    : String(val)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-slate-50 rounded-lg border border-slate-200 border-dashed">
                      <p className="text-slate-500 font-medium">
                        A consulta retornou 0 registros.
                      </p>
                      <p className="text-sm text-slate-400 mt-1">
                        O comando SQL foi executado com sucesso, mas não há
                        dados nesse período ou filtro.
                      </p>
                    </div>
                  )}

                  {previewData.sql && (
                    <div className="space-y-2 mt-4">
                      <h4 className="font-medium text-slate-900 text-sm">
                        SQL Compilado
                      </h4>
                      <pre className="p-3 bg-slate-900 text-slate-100 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap break-words font-mono">
                        {previewData.sql}
                      </pre>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-slate-500 text-center py-10">
                    Nenhum dado disponível
                  </p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ExtractionsPage;
